import Skeleton from '@material-ui/lab/Skeleton';
import classNames from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { ThemeContext } from '@swf/core/providers/ThemeProvider';
import { select } from '@swf/core/store';
import type { Reference } from '@swf/generated/cms/types';

import CMSReferences from '../../CMSReference/CMSReferences';
import AuthRequiredPage from '../AuthRequiredPage/AuthRequiredPage';

import styles from './PageBody.scss';

interface Props {
  loading: boolean;
  twoColumns: boolean;
  darkTheme: boolean;
  main: Reference[];
  side: Reference[];
}

const PageBody: React.FC<Props> = ({ twoColumns, main, side, loading, darkTheme }) => {
  const authenticated = useSelector(select.session.isAuthenticated);
  const isServerAuthenticated = useSelector(select.session.isServerAuthenticated);
  const authRequired = useSelector(select.page.isAuthRequired);
  const { darkClassName } = useContext(ThemeContext);
  const { hash } = useLocation();
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);

  useEffect(() => {
    const scrollToAnchor = () => {
      if (!loading && hash) {
        const element = document.getElementById(hash.replace('#', ''));
        element?.scrollIntoView();
      }
    };

    if (document.readyState === 'complete') {
      scrollToAnchor();
    } else {
      window.addEventListener('load', scrollToAnchor);
      return () => window.removeEventListener('load', scrollToAnchor);
    }
  }, [loading, hash]);

  if (authRequired && !authenticated) {
    return <AuthRequiredPage />;
  }

  if (loading || (authRequired && isServerAuthenticated)) {
    return (
      <div className={styles.mainSkeleton}>
        <Skeleton variant="rect" animation="wave" component="h1" />
        <Skeleton variant="rect" animation="wave" component="h2" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
        <Skeleton variant="rect" animation="wave" component="h2" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.body, darkTheme && darkClassName, {
        [styles.twoColumns]: twoColumns,
        [styles.embeddedContainer]: isEmbeddedRoute
      })}
    >
      <div className={styles.main}>
        <CMSReferences references={main} />
      </div>
      {twoColumns && (
        <aside className={styles.side}>
          <CMSReferences references={side} />
        </aside>
      )}
    </div>
  );
};

export default PageBody;
